import { useEffect, useState, type MouseEvent } from 'react'
import { useSessionUser } from '@/hooks/use-session'
import { GoogleIcon } from '@/routes/_app/-components/assets/google-icon'
import { LinkedInIcon } from '@/routes/_app/-components/assets/linkedin-icon'
import { trpc } from '@/main'
import { useMutation } from '@tanstack/react-query'
import { useAppStore } from '@/lib/app-store'
import { useLingui } from '@lingui/react/macro'
import { CheckIcon, LoaderCircleIcon, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

type SocialPageTypes = 'loginGoogle' | 'loginLinkedin'

// Account metadata is window-free so the buttons can render during SSR (no layout shift). Only the
// OAuth href — which needs the injected client-id globals and window.location — is built client-side.
const ACCOUNTS = [
  {
    key: 'google' as const,
    loginKey: 'loginGoogle' as const,
    name: 'Google',
    icon: GoogleIcon,
    iconClass: 'size-5',
  },
  {
    key: 'linkedin' as const,
    loginKey: 'loginLinkedin' as const,
    name: 'LinkedIn',
    icon: LinkedInIcon,
    iconClass: 'size-5',
  },
]

function buildHrefs(socialLoginState: string, nonce: string): Record<'google' | 'linkedin', string> {
  return {
    google: `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
      client_id: window.GOOGLE_CLIENT_ID,
      redirect_uri: `${window.location.origin}/api/v2/auth/google`,
      scope: [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ].join(' '),
      response_type: 'code',
      // We only verify the id_token, so we don't request offline access or force consent — those
      // re-grant on every login (and Google emails a notification each time). 'select_account' keeps
      // the account picker without the repeated grant.
      prompt: 'select_account',
      // OpenID nonce: Google echoes it into the id_token, and the callback checks it matches the
      // server-minted value (same value carried in state.csrf) — binding the token to this login.
      nonce,
      state: socialLoginState,
    })}`,
    linkedin: `https://www.linkedin.com/oauth/v2/authorization?${new URLSearchParams({
      client_id: window.LINKEDIN_CLIENT_ID,
      redirect_uri: `${window.location.origin}/api/v2/auth/linkedin`,
      scope: ['r_liteprofile', 'r_emailaddress', 'w_member_social'].join(' '),
      response_type: 'code',
      state: socialLoginState,
    })}`,
  }
}

export function SocialButtons(props: {
  register?: boolean
  login?: boolean
  exclude?: Array<'google' | 'linkedin'>
  // Invite (set-password) token carried through OAuth so the callback links the company's stub into
  // the social-resolved account (rescuing the test).
  linkToken?: string
}) {
  const { t } = useLingui()
  const clientId = useAppStore((state) => state.store.clientId)
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())
  const [loading, setLoading] = useState<string>()

  // OAuth hrefs need the injected client-id globals + window.location, so they can only be built on the
  // client. We compute them after mount (SSR and the first client render stay identical → no hydration
  // mismatch) while the button structure itself renders on the server — so the buttons reserve their
  // space immediately and never shift the layout in (the previous null-until-mounted gate caused CLS).
  const [hrefs, setHrefs] = useState<Record<'google' | 'linkedin', string>>()
  const [envMissing, setEnvMissing] = useState(false)
  useEffect(() => {
    if (window.GOOGLE_CLIENT_ID === '{{googleClientId}}' || window.LINKEDIN_CLIENT_ID === '{{linkedinClientId}}') {
      console.info('Social environment variables not defined')
      // oxlint-disable-next-line react/set-state-in-effect -- hydration parity: OAuth hrefs need window-injected client-id globals, so SSR and the first client render must both be href-less (see the comment above)
      setEnvMissing(true)
      return
    }
    let cancelled = false
    // CSRF/nonce: mint a single-use nonce server-side (set in an HttpOnly cookie) and echo it back in
    // `state.csrf` + Google's OpenID `nonce`. The callback rejects any mismatch, so a forged callback
    // can't mint a session. We only expose the hrefs once the nonce is in hand — a failed mint leaves
    // the buttons non-navigating rather than starting a flow the callback would reject.
    void (async () => {
      let nonce: string
      try {
        const res = await fetch('/api/v2/auth/oauth-state', { credentials: 'include' })
        nonce = ((await res.json()) as { state: string }).state
      } catch {
        return
      }
      if (cancelled || !nonce) return
      const socialLoginState = JSON.stringify({
        // Capture the /login page URL verbatim (it's the auth-entry wrapper `/login?redirect=X` the server's
        // resolveSocialDestination expects): the server unwraps the nested target and applies the onboarding
        // gates. Never emit empty — an empty redirect collapses to the callback's non-browser JSON tail.
        redirect: encodeURIComponent(window.location.pathname + window.location.search),
        clientId,
        // The origin behind the authorize redirect_uri; the callback must reuse it verbatim for the token
        // exchange (exact match required), and behind the dev proxy the backend can't recover it.
        origin: window.location.origin,
        csrf: nonce,
        ...(props.linkToken ? { linkToken: props.linkToken } : {}),
      })
      setHrefs(buildHrefs(socialLoginState, nonce))
    })()
    return () => {
      cancelled = true
    }
  }, [clientId, props.linkToken])

  if (envMissing) return null

  const accounts = ACCOUNTS.filter((account) => !props.exclude?.includes(account.key))

  const handleSocialPage = async (
    event: MouseEvent<HTMLAnchorElement>,
    socialpageIndex: SocialPageTypes,
    socialpage: 'google' | 'linkedin',
  ) => {
    if (!user?.[socialpageIndex] || loading === socialpage) return

    event.preventDefault()
    setLoading(socialpage)
    await updateUser({ [socialpage]: null })
    setLoading(undefined)
  }

  // Profile view: show as bordered buttons with status
  const isProfileView = !props.login && !props.register && user

  return (
    <div className="flex flex-col gap-2">
      {accounts.map((account) => {
        const Icon = account.icon
        const isLoading = isProfileView && loading === account.key
        const connected = isProfileView && user[account.loginKey]

        return (
          <a
            key={account.key}
            className={cn(
              'group flex items-center justify-center gap-3 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors',
              connected
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-400 hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-600 dark:hover:text-red-400'
                : 'bg-background hover:bg-muted',
            )}
            onClick={(event) => handleSocialPage(event, account.loginKey, account.key)}
            href={hrefs?.[account.key]}
          >
            <div className="flex size-5 items-center justify-center">
              {isLoading ? (
                <LoaderCircleIcon className="size-4 animate-spin" />
              ) : connected ? (
                <>
                  <CheckIcon className="size-4 group-hover:hidden" />
                  <XIcon className="hidden size-4 group-hover:block" />
                </>
              ) : (
                <Icon className={account.iconClass} />
              )}
            </div>
            {connected ? (
              <span className="grid text-center">
                <span className="col-start-1 row-start-1 group-hover:invisible">
                  {t`Linked with`} {account.name}
                </span>
                <span className="invisible col-start-1 row-start-1 group-hover:visible">
                  {t`Unlink from`} {account.name}
                </span>
              </span>
            ) : (
              <span>
                {isProfileView ? t`Link with` : t`Continue with`} {account.name}
              </span>
            )}
          </a>
        )
      })}
    </div>
  )
}
